import { createContext, useContext } from "react";
import { CallState, Experiment, ExperimentState } from "redmond";

type DefaultVariantNames = "control" | "available";
type LDOnlyConfig<Id extends string, VariantName = DefaultVariantNames> = {
  id: Id;
  variant: VariantName;
  expirationDate?: string;
};

type LDOnlyJsonConfig<
  Id extends string,
  Datum extends object,
  VariantName = DefaultVariantNames
> = LDOnlyConfig<Id, VariantName> & {
  data: Datum;
};

export type UntypedExperiment = {
  id: string;
  variant: string;
  expirationDate?: string;
};

export type CorporateExperimentByBusiness = {
  id: string;
  variant: string;
  eligibleAccountRefId?: string;
};

export type ActiveFlagsResponse = {
  experiments: UntypedExperiment[];
  trackingProperties: Record<string, string>;
};

export type CorpExperimentsMap = {
  "corp-business-tabs": LDOnlyConfig<
    "corp-business-tabs",
    "control" | "available"
  >;
  "corp-traveler-maintenance": LDOnlyJsonConfig<
    "corp-traveler-maintenance",
    {
      title: string;
      subtext: string;
      severity: "error" | "warning" | "info" | "success";
    },
    "banner" | "page" | "control"
  >;
  "corp-traveler-hotel-itemized-receipt": LDOnlyConfig<"corp-traveler-hotel-itemized-receipt">;
  "corp-dynamic-limits": LDOnlyConfig<"corp-dynamic-limits">; // Repurposed for showing the corporate debugging pannel
  "c1-corporate-credit-card-validation": LDOnlyConfig<
    "c1-corporate-credit-card-validation",
    "control" | "any-spark"
  >;
  "corp-hide-price-drop-protection": LDOnlyConfig<"corp-hide-price-drop-protection">;
  "corp-amadeus-multiroom": LDOnlyConfig<"corp-amadeus-multiroom">;
  "hbux-pricing-all-in": LDOnlyConfig<"hbux-pricing-all-in">;
  "c1-marketplace-trees": LDOnlyConfig<"c1-marketplace-trees">;
  "c1-marketplace-travel-wallet-offers": LDOnlyConfig<"c1-marketplace-travel-wallet-offers">;
  "c1-marketplace-credit-offer-stacking-v1": LDOnlyConfig<"c1-marketplace-credit-offer-stacking-v1">;
  "c1-marketplace-credit-transaction-history": LDOnlyConfig<"c1-marketplace-credit-transaction-history">;
  "c1-marketplace-travel-wallet-credits": LDOnlyConfig<"c1-marketplace-travel-wallet-credits">;
  "c1-auto-select-card-at-checkout": LDOnlyConfig<
    "c1-auto-select-card-at-checkout",
    "control" | "available"
  >;
  "c1-marketplace-HotelsCaliforniaBill644": LDOnlyConfig<"c1-marketplace-HotelsCaliforniaBill644">;
  "b2b-agency-air-multicity": LDOnlyConfig<"b2b-agency-air-multicity">;
  "corp-sean-ellis-question": LDOnlyConfig<
    "corp-sean-ellis-question",
    "control" | "available" | "debug"
  >;
  "c1-fintech-hotel-cfar": LDOnlyConfig<"c1-fintech-hotel-cfar">;
  "corp-trip-purpose": LDOnlyConfig<"corp-trip-purpose">;
  "corp-expense-management": LDOnlyConfig<"corp-expense-management">;
  "c1-fintech-cfar-hotel-continue-checkout-text-enable": LDOnlyConfig<
    "c1-fintech-cfar-hotel-continue-checkout-text-enable",
    "control" | "available"
  >;
  "corp-non-financial-users": LDOnlyConfig<"corp-non-financial-users">;
  "c1-marketplace-global-mobile-nav": LDOnlyConfig<"c1-marketplace-global-mobile-nav">;
  "c1-corporate-spark-migration-banner": LDOnlyConfig<
    "c1-corporate-spark-migration-banner",
    "control" | "available" | "debug" | "test-launch-date"
  >;
  "corp-force-cfar-error-modal": LDOnlyConfig<
    "corp-force-cfar-error-modal",
    "control" | "available"
  >;
  "corp-force-fda-error-modal": LDOnlyConfig<
    "corp-force-fda-error-modal",
    "control" | "available"
  >;
  "corp-hotel-book-again": LDOnlyConfig<
    "corp-hotel-book-again",
    "control" | "available"
  >;
  "corp-checkout-rewards-earn-banner": LDOnlyConfig<
    "corp-checkout-rewards-earn-banner",
    "control" | "available" | "flights-earn"
  >;
  "corp-hotel-room-rates-design-test-v1": LDOnlyConfig<
    "corp-hotel-room-rates-design-test-v1",
    "control" | "available"
  >;
  "c1-stays-homepage": LDOnlyConfig<
    "c1-stays-homepage",
    "control" | "available"
  >;
  "corp-onboarding-revamp": LDOnlyConfig<
    "corp-onboarding-revamp",
    "control" | "available" | "debug"
  >;
  "corp-primary-welcome-modal-revamp": LDOnlyConfig<
    "corp-primary-welcome-modal-revamp",
    "control" | "available"
  >;
  "corp-business-loyalty-refresh": LDOnlyConfig<
    "corp-business-loyalty-refresh",
    "control" | "available"
  >;
  "corp-cobt-em-modal": LDOnlyConfig<
    "corp-cobt-em-modal",
    "control" | "available"
  >;
  "c1-corporate-vxb-migration-takeover-force-show": LDOnlyConfig<
    "c1-corporate-vxb-migration-takeover-force-show",
    "control" | "force-show" | "force-hide"
  >;
  "c1-corporate-vxb-migration-takeover": LDOnlyConfig<
    "c1-corporate-vxb-migration-takeover",
    "control" | "available"
  >;
  "c1-replatform-hotels-funnel": LDOnlyConfig<
    "c1-replatform-hotels-funnel",
    "control" | "available"
  >;
  "c1-replatform-packages-funnel": LDOnlyConfig<
    "c1-replatform-packages-funnel",
    "control" | "production" | "available"
  >;
  "c1-homepage-redesign": LDOnlyConfig<
    "c1-homepage-redesign",
    "control" | "available" | "available-funnel-ordering"
  >;
  "c1-marketplace-xmod-v1": LDOnlyConfig<
    "c1-marketplace-xmod-v1",
    "control" | "available"
  >;
  "hide-disruption-sms-notifications": LDOnlyConfig<
    "hide-disruption-sms-notifications",
    "control" | "available"
  >;
  "hide-disruption-app-notifications": LDOnlyConfig<
    "hide-disruption-app-notifications",
    "control" | "available"
  >;
  "corp-free-fda-account-preselect": LDOnlyJsonConfig<
    "corp-free-fda-account-preselect",
    { tags: string[] },
    "control" | "available"
  >;
  "c1-vesper-pc-credit": LDOnlyConfig<"c1-vesper-pc-credit", "control" | "available">;
  "c1-marketplace-luxury-hotels-collection": LDOnlyConfig<
    "c1-marketplace-luxury-hotels-collection",
    "control" | "available"
  >;
  "c1-single-day-round-trip-flights": LDOnlyConfig<
    "c1-single-day-round-trip-flights",
    "control" | "available"
  >;
  "corp-embedded-traveler-form": LDOnlyConfig<
    "corp-embedded-traveler-form",
    "control" | "available"
  >;
};

export const BUSINESS_EXPERIMENT_VARIANTS = {
  "corp-intra-business-referrals": [
    "control",
    "available",
    "available-100",
    "earn-banner",
  ],
  "corp-free-fda": ["control", "available", "available_phase2"],
  "corp-cobt-em-modal": ["control", "available"],
} as const;

export type CorpBusinessExperimentsMap = {
  [K in keyof typeof BUSINESS_EXPERIMENT_VARIANTS]: LDOnlyConfig<
    K,
    (typeof BUSINESS_EXPERIMENT_VARIANTS)[K][number]
  >;
};

export type ConsumerExperimentsMap = {
  "cap1-portal-maintenance": LDOnlyJsonConfig<
    "cap1-portal-maintenance",
    {
      title: string;
      subtext: string;
      severity: "error" | "warning" | "info" | "success";
    },
    "banner" | "page" | "control"
  >;
  "c1-marketplace-xmod-v1": LDOnlyConfig<
    "c1-marketplace-xmod-v1",
    "control" | "available"
  >;
  "c1-marketplace-annual-travel-credits": LDOnlyConfig<
    "c1-marketplace-annual-travel-credits",
    "control" | "available"
  >;
  "c1-auto-select-card-at-checkout": LDOnlyConfig<
    "c1-auto-select-card-at-checkout",
    "control" | "available"
  >;
  "cars-hertz-loyalty-section": LDOnlyConfig<"cars-hertz-loyalty-section">;
  "corp-free-fda": LDOnlyConfig<"corp-free-fda">;
  "c1-vesper-pc-credit": LDOnlyConfig<"c1-vesper-pc-credit">;
  "c1-marketplace-luxury-hotels-collection": LDOnlyConfig<
    "c1-marketplace-luxury-hotels-collection",
    "control" | "available"
  >;
  "c1-combined-rewards-checkout": LDOnlyConfig<
    "c1-combined-rewards-checkout",
    "control" | "available"
  >;
};
// Default variants
export const CONTROL = "control";
export const AVAILABLE = "available";
export const VARIANT_1 = "variant-1";
export const VARIANT_2 = "variant-2";

export const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
export type DefaultVariantType = typeof CONTROL | typeof AVAILABLE;

// readonly (together with const restriction below) ensures typeof supportedVariants[number]
// can be recognized by transpiler as an array of constants
// [string, ...string[]] ensures array is not empty.
export function getExperimentVariantCustomVariants<
  T extends readonly [string, ...string[]]
>(
  experiments: Array<Experiment>,
  experimentId: string,
  // List of supported variants. If the variant retreived is not recognized by the code, the code will fall back to the first variant.
  // Note the input list needs to be declared as const e.g. const DEFAULT_VARIANTS = [CONTROL, AVAILABLE] as const;
  supportedVariants: T
): (typeof supportedVariants)[number] {
  const experiment = experiments?.find((exp) => {
    if (exp.id === experimentId) {
      return exp;
    }
    return null;
  });

  if (experiment) {
    if (supportedVariants.includes(experiment.variant)) {
      return experiment.variant;
    }
    return supportedVariants[0];
  }
  return supportedVariants[0];
}

export function getExperimentVariant(
  experiments: Array<Experiment>,
  experimentId: string
): string {
  const experiment = experiments?.find((exp) => {
    if (exp.id === experimentId) {
      return exp;
    }

    return null;
  });

  if (experiment) {
    return experiment.variant;
  }
  return CONTROL;
}

export function getDefaultExperimentVariant(
  experiments: Array<Experiment>,
  experimentId: string
): DefaultVariantType {
  return getExperimentVariantCustomVariants(
    experiments,
    experimentId,
    DEFAULT_VARIANTS
  );
}

interface ExperimentStateWithCallState extends ExperimentState {
  callState: CallState;
}

export const ExperimentsContext = createContext<
  ExperimentStateWithCallState | undefined
>(undefined);

export function useExperimentsContext(): ExperimentStateWithCallState {
  const ctx = useContext(ExperimentsContext);
  if (!ctx) throw new Error(`must be used within a ExperimentsProvider`);
  return ctx;
}

// CapOne experiments
export const LUXURY_COMPLIMENTARY_NIGHT_PROMOTION =
  "c1-luxury-complimentary-night-promotion";
export const LUXURY_COMPLIMENTARY_NIGHT_PROMOTION_ENTRYPOINTS =
  "c1-luxury-complimentary-night-promotion-entrypoints";
export const PREMIER_COLLECTION_EXPERIMENT =
  "c1-marketplace-luxury-hotels-collection";
export const LUXURY_NOVEMBER_VACATION_RENTAL_OFFER =
  "c1-luxury-NovemberVacationRentalOffer";

export const STAYS_WISHLIST = "c1-marketplace-wishlists";
export const STAYS_WISHLIST_FTUX = "c1-marketplace-wishlists-ftux";

export const VESPER_PC_CREDIT = "c1-vesper-pc-credit";

